import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import CompactListingCard from '../components/CompactListingCard';
import './BoostSelectPage.css';

const PACKAGES = {
  boost_single: { cap: 1, totalAmount: 10.99, priceLabel: '€10.99' },
};

const PACKAGE_KEY = 'boost_pkg';
const SELECTION_KEY = 'boost_selection';

function readStoredPackage() {
  try {
    const v = sessionStorage.getItem(PACKAGE_KEY);
    if (v && PACKAGES[v]) return v;
  } catch (_) {/* ignore */}
  return null;
}

function readStoredSelection() {
  try {
    const raw = sessionStorage.getItem(SELECTION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_) {
    return [];
  }
}

function IconHome() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export default function BoostSelectPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Source order: route state → URL ?package= → sessionStorage. If still nothing,
  // bounce to Screen 1 so the user picks a package first.
  const packageKey = useMemo(() => {
    const fromState = location.state?.package;
    if (fromState && PACKAGES[fromState]) return fromState;
    const fromUrl = searchParams.get('package');
    if (fromUrl && PACKAGES[fromUrl]) return fromUrl;
    return readStoredPackage();
  }, [location.state, searchParams]);

  useEffect(() => {
    if (!packageKey) {
      navigate('/boost', { replace: true });
      return;
    }
    try { sessionStorage.setItem(PACKAGE_KEY, packageKey); } catch (_) {}
  }, [packageKey, navigate]);

  // Defensive fallback if redirect hasn't kicked in yet
  const pkg = packageKey ? PACKAGES[packageKey] : PACKAGES.boost_single;
  const cap = pkg.cap;

  const preselectId = searchParams.get('preselect');

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => {
    if (preselectId) return [String(preselectId)];
    return readStoredSelection();
  });
  const [showMaxNotice, setShowMaxNotice] = useState(false);

  // Persist selection (survives refresh / Stripe cancel)
  useEffect(() => {
    try { sessionStorage.setItem(SELECTION_KEY, JSON.stringify(selectedIds)); } catch (_) {}
  }, [selectedIds]);

  useEffect(() => {
    if (!user || !user.id) return;
    let cancelledFetch = false;
    setLoading(true);
    supabase
      .from('listings')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (cancelledFetch) return;
        setListings(data || []);
        setLoading(false);
      });
    return () => { cancelledFetch = true; };
  }, [user?.id]);

  const isExpired = (date) => !date || new Date(date) < new Date();
  const daysLeft = (date) => Math.max(0, Math.ceil((new Date(date) - new Date()) / 86400000));
  const isAlreadyBoosted = (l) => l.is_boosted && !isExpired(l.boost_expires_at);

  // Drop persisted IDs that no longer exist or are already boosted; trim to cap
  useEffect(() => {
    if (loading) return;
    setSelectedIds(prev => {
      const valid = prev.filter(sid => {
        const row = listings.find(l => String(l.id) === sid);
        return row && !isAlreadyBoosted(row);
      });
      return valid.slice(0, cap);
    });
  }, [loading, listings, cap]);

  const toggle = (id) => {
    const sid = String(id);

    if (cap === 1) {
      // Single-select: clicking any unselected card replaces the selection.
      setSelectedIds([sid]);
      return;
    }

    setSelectedIds(prev => {
      if (prev.includes(sid)) {
        return prev.filter(x => x !== sid);
      }
      if (prev.length >= cap) {
        setShowMaxNotice(true);
        window.setTimeout(() => setShowMaxNotice(false), 2400);
        return prev;
      }
      return [...prev, sid];
    });
  };

  const atCap = selectedIds.length >= cap;
  const meetsRequirement = cap === 1 ? selectedIds.length === 1 : selectedIds.length >= 1;

  const bundleLabel = `${t('boostPage.packageListingLabel')} · ${t('boostPage.packageDuration')}`;
  const subText = cap === 1
    ? t('boostSelect.sub')
    : t('boostSelectPage.pickUpTo').replace('{cap}', String(cap));

  const handleNext = () => {
    if (!meetsRequirement) return;
    navigate('/boost-checkout', {
      state: { package: packageKey, listingIds: selectedIds },
    });
  };

  if (!user) {
    return (
      <div className="boost-select">
        <div className="boost-select__inner">
          <div className="boost-select__signin">
            <h1>{t('boostSelect.signInTitle')}</h1>
            <p>{t('boostSelect.signInSub')}</p>
            <button
              className="boost-select__primary-btn"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/boost-select?package=${packageKey || ''}`)}`)}
            >
              {t('boostSelect.signIn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="boost-select">
      <div className="boost-select__inner">
        <button className="boost-select__back" onClick={() => navigate('/boost')}>
          {t('boostSelectPage.back')}
        </button>

        <header className="boost-select__header">
          <h1 className="boost-select__title">{t('boostSelectPage.title')}</h1>
          <p className="boost-select__sub">{subText}</p>
          <div className="boost-select__chosen-pack">
            <span className="boost-select__chosen-label">{t('boostSelectPage.packageLabel')}</span>
            <span className="boost-select__chosen-value">{bundleLabel} · {pkg.priceLabel}</span>
          </div>
        </header>

        {loading ? (
          <div className="boost-select__loading">{t('boostSelect.loading')}</div>
        ) : listings.length === 0 ? (
          <div className="boost-select__empty">
            <div className="boost-select__empty-icon"><IconHome /></div>
            <h3>{t('boostSelect.noListings')}</h3>
            <p>{t('boostSelect.noListingsSub')}</p>
            <button className="boost-select__primary-btn" onClick={() => navigate('/list-property')}>
              {t('boostSelect.noListingsCta')}
            </button>
          </div>
        ) : (
          <>
            {showMaxNotice && (
              <div className="boost-select__max-notice" role="status">
                {t('boostSelect.maxNotice').replace('3', String(cap))}
              </div>
            )}

            <div className="boost-select__grid">
              {listings.map(l => {
                const boosted = isAlreadyBoosted(l);
                const selected = selectedIds.includes(String(l.id));
                const disabled = cap === 1
                  ? boosted
                  : (boosted || (atCap && !selected));
                return (
                  <CompactListingCard
                    key={l.id}
                    listing={l}
                    mode="select"
                    isAlreadyBoosted={boosted}
                    daysLeft={daysLeft(l.boost_expires_at)}
                    selected={selected}
                    disabled={disabled}
                    onToggle={toggle}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {!loading && listings.length > 0 && (
        <div className="boost-select__bar">
          <div className="boost-select__bar-inner">
            <div className="boost-select__bar-info">
              <div className="boost-select__bar-count">
                {t('boostSelectPage.selectedOfCap')
                  .replace('{n}', String(selectedIds.length))
                  .replace('{cap}', String(cap))}
              </div>
              <div className="boost-select__bar-meta">{bundleLabel}</div>
            </div>
            <button
              type="button"
              className="boost-select__cta"
              onClick={handleNext}
              disabled={!meetsRequirement}
            >
              {t('boostSelectPage.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
